import React, { Fragment, useState } from 'react';
import { Alert } from '@interstate/components/Alert';
import { AuxButton } from '@interstate/components/Button';
import { Typography } from '@interstate/components/Typography';

import { BannerWrapper } from '../Banner.interstate.style';

export interface AlertBannerProps {
    type: 'info' | 'success' | 'caution' | 'error';
    title?: any;
    message: string | React.ReactNode;
    dataTestId: string;
    dismissButtonText?: string;
    disableSuccessFade?: boolean;
}

/**
 * Alert Banner Component
 *
 * A reusable banner component that follows the established pattern
 * for banners in the application (RecalculateBanner, DroppedFeesBanner, etc.)
 *
 * @param type - Alert type (info, success, warning, caution, error)
 * @param title - Optional title for the alert
 * @param message - Message content (string or React node)
 * @param dataTestId - Test ID for the alert
 * @param dismissButtonText - Optional custom text for dismiss button (default: "Okay, Got It")
 * @param disableSuccessFade - Whether to disable auto-fade for success alerts
 */
const AlertBanner: React.FC<AlertBannerProps> = ({
    type,
    title,
    message,
    dataTestId,
    dismissButtonText,
    disableSuccessFade = false
}) => {
    const [show, setShow] = useState(true);

    const handleDismiss = () => {
        setShow(false);
    };

    if (!show) {
        return null;
    }

    return (
        <Fragment>
            <BannerWrapper className="isBaseDeal">
                <Alert
                    type={type}
                    key={`alert-${dataTestId}`}
                    data-testid={dataTestId}
                    title={title}
                    disableSuccessFade={type === 'success' ? disableSuccessFade : undefined}
                >
                    <Typography variant="body-sm">
                        {message}
                        {dismissButtonText && (
                            <AuxButton data-testid={`${dataTestId}-dismiss-button`} onClick={handleDismiss}>
                                {dismissButtonText}
                            </AuxButton>
                        )}
                    </Typography>
                </Alert>
            </BannerWrapper>
        </Fragment>
    );
};

export default AlertBanner;
